import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { products } from "../mockData";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Package,
  FileText,
} from "lucide-react";
import QuickInquiry from "../components/QuickInquiry";
import { useToast } from "../hooks/use-toast";
import Seo from "../components/Seo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import "swiper/css";
import "swiper/css/pagination";

const productSeoMap = {
  "Acid Dyes": {
    title: "Acid Dyes Manufacturer in India | Exporter to Bangladesh & Vietnam",
    description:
      "We are a leading acid dyes manufacturer in India, offering high-quality dyes for silk and wool fabric dyeing, including Acid Red 88, Acid Blue 25, and Acid Yellow 36. We export eco-friendly textile dyeing chemicals to Bangladesh and Vietnam.",
    keywords:
      "Acid Dyes Manufacturers, Acid Red 88, Acid Blue 25, Acid Yellow 36 Exporters, Acid Red Exporters, Acid Black Manufacturers",
    canonical: "https://meet-enterprise.com/silk-dyes",
  },
  "Reactive Dyes": {
    title: "Reactive Dyes Manufacturer in India | Exporter to Bangladesh & Vietnam",
    description:
      "We are a leading manufacturer of reactive dyes for cotton fabric dyeing, offering products such as Reactive Blue 19, Reactive Red 120, and Reactive Yellow 145 with excellent color fastness. We export to Bangladesh and Vietnam.",
    keywords:
      "Reactive Dyes Manufacturers, Reactive Dyes Exporters, Reactive Blue 19, Reactive Red 120, Reactive Yellow 145 for Textiles, Reactive Black Suppliers, Gujarat, India",
    canonical: "https://meet-enterprise.com/cotton-dyes",
  },
  "Direct Dyes": {
    title: "Direct Dyes Manufacturer in India | Textile & Paper Dyeing Exporter",
    description:
      "Reliable supplier of direct dyes for cotton and paper dyeing, including Direct Yellow 12, Direct Blue 86, and Direct Red 28. Exporting high-performance dyeing chemicals to global textile industries.",
    keywords:
      "Direct Dyes, Direct Black, Direct Black 22, Direct Blue 201, Direct Yellow, Direct Orange, Direct Blue, Direct Green, Direct Red, Direct Violet",
    canonical: "https://meet-enterprise.com/cotton-and-paper-dyes",
  },
};

const productLabReportMap = {
  "Acid Dyes": "/acid_lab_report.pdf",
  "Reactive Dyes": "/reactive_lab_report.pdf",
  "Direct Dyes": "/direct_lab_report.pdf",
};

const ProductDetail = () => {
  const { productSlug } = useParams();
  // Find product by matching the slug (without leading slash)
  const product = products.find((p) => {
    const pathSlug = p.path.startsWith("/") ? p.path.substring(1) : p.path;
    return pathSlug === productSlug;
  });
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [labCertificateOpen, setLabCertificateOpen] = useState(false);
  const { toast } = useToast();

  const labReportUrl = productLabReportMap[product?.title] || "/lab-reports.pdf";

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Product Not Found
          </h2>
          <Link to="/" className="text-purple-600 hover:text-purple-700">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleDownload = () => {
    toast({
      title: "Download Started",
      description: "Product catalog is being downloaded...",
    });
  };

  return (
    <div className="min-h-screen">
      <Seo
        title={productSeoMap[product.title]?.title || `${product.title} | Meet Enterprise`}
        description={productSeoMap[product.title]?.description || product.description}
        keywords={productSeoMap[product.title]?.keywords}
        canonical={productSeoMap[product.title]?.canonical}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-600 to-pink-600 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-white hover:text-opacity-80 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {product.title}
            </h1>

            <p className="text-xl text-white text-opacity-90">
              {product.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur-2xl opacity-20"></div>

                <Swiper
                  modules={[Autoplay, Pagination]}
                  slidesPerView={1}
                  loop={true}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                  }}
                  pagination={{
                    clickable: true,
                    bulletClass: 'swiper-pagination-bullet',
                    bulletActiveClass: 'swiper-pagination-bullet-active',
                  }}
                  className="w-full rounded-2xl shadow-2xl"
                  style={{
                    '--swiper-pagination-color': '#9333ea',
                    '--swiper-pagination-bullet-inactive-color': '#ffffff',
                    '--swiper-pagination-bullet-inactive-opacity': '0.5',
                    '--swiper-pagination-bullet-opacity': '1',
                    '--swiper-pagination-bullet-size': '8px',
                    '--swiper-pagination-bullet-horizontal-gap': '6px',
                  }}
                >
                  {Array.isArray(product.image) ? product.image.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={img}
                        alt={`${product.title} ${idx + 1}`}
                        className="w-full h-auto rounded-2xl"
                      />
                    </SwiperSlide>
                  )) : (
                    <SwiperSlide>
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-auto rounded-2xl"
                      />
                    </SwiperSlide>
                  )}
                </Swiper>
              </div>

              {/* Color Palette */}
              {product.colorPalette && (
                <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Our Best Sellers
                  </h3>

                  <div className="flex gap-3">
                    {product.colorPalette?.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-center flex-1">
                        <div
                          className="w-12 h-12 rounded-lg shadow-md border-2 border-white hover:scale-110 transition-transform cursor-pointer mx-auto"
                          style={{ backgroundColor: item.color }}
                          title={item.name}
                        ></div>

                        <span className="text-xs text-gray-600 mt-1 block break-words leading-tight">
                          {item.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-gray-600 mt-4">
                    *Colors shown are representative. Custom color matching
                    available upon request.
                  </p>
                </div>
              )}

              {/* Sub Types */}
              {product.subTypes && (
                <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Available Product Series
                  </h3>

                  <div className="flex flex-wrap gap-3">
                    {product.subTypes.map((type, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition"
                      >
                        <span className="text-sm font-semibold text-gray-900">
                          {type.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quality Standard */}
              <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <span>Quality Standard</span>
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  All our products undergo rigorous testing and comply with international quality standards. Our dedicated QA & QC systems minimizes errors at every stage, while advanced spectrophotometer-based testing ensures each batch precisely matches buyer specifications, delivering consistent quality across every supply.
                </p>
              </div>

              {/* Packaging */}
              <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-6 h-6 text-blue-600" />
                  <span>Packaging</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      <span className="font-semibold">Standard packing:</span> 25 kg HDPE bags.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      <span className="font-semibold">Also available:</span> corrugated box and drum packaging (export-focused), along with fully customised solutions as per client needs.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      Built to ensure moisture protection, export-grade strength, and safe international transit.
                    </p>
                  </div>
                </div>
              </div>

              {/* MOQ */}
              <div className="mt-8 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  MOQ (Minimum Order Quantity)
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-amber-200">
                    <span className="text-gray-700 font-medium">Per pallet</span>
                    <span className="text-gray-900 font-bold">700 kgs</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-amber-200">
                    <span className="text-gray-700 font-medium">20 ft container</span>
                    <span className="text-gray-900 font-bold">12,000 kgs</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-700 font-medium">40 ft container</span>
                    <span className="text-gray-900 font-bold">25,000 kgs</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-4 italic">
                  *Note: The above quantities are approximate and may vary based on product type and packaging. Kindly consult with us before finalising your order.
                </p>
              </div>
            </motion.div>

            {/* Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="sticky top-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Product Overview
                </h2>

                <p className="text-lg text-gray-600 mb-8">
                  {product.description}
                </p>

                {/* Key Features */}
                {product.features && (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Key Features
                    </h3>

                    <ul className="space-y-3 mb-8">
                      {product.features?.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* Lab Report */}
                <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-purple-600" />
                    <span>Lab Report</span>
                  </h3>
                  <p className="text-gray-700 mb-4">
                    View the product-specific lab report for detailed quality and test results.
                  </p>
                  <button
                    onClick={() => setLabCertificateOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-md"
                  >
                    <FileText className="w-5 h-5" />
                    <span>View Lab Report</span>
                  </button>
                </div>

                {/* Technical Specifications */}
                {/* {product.technicalSpecifications &&
                  product.technicalSpecifications.length > 0 && (
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-100">
                      <h3 className="text-2xl font-bold text-gray-900 mb-5">
                        Technical Specifications
                      </h3>
                      <div className="divide-y divide-gray-200">
                        {product.technicalSpecifications.map((spec, index) => (
                          <div
                            key={index}
                            className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                          >
                            <span className="text-sm font-semibold text-gray-800">
                              {spec.label}
                            </span>
                            <span className="text-sm text-gray-600">
                              {spec.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )} */}

                <div className="space-y-4">
                  <button
                    onClick={() => setInquiryOpen(true)}
                    className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all"
                  >
                    Request for a Shade Card
                  </button>
                </div>

                <div className="mt-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-6 h-6 text-purple-600" />
                    <span>Industries Served</span>
                  </h4>

                  <p className="text-gray-600">
                    Our {product.title.toLowerCase()} are used across various
                    industries including textiles, fashion, Home Furnishing,
                    Interior Textile & Leather. We provide customized solutions
                    for bulk orders.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Other Products
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products
              .filter((p) => p.id !== product.id)
              .slice(0, 3)
              .map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={relatedProduct.path}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={Array.isArray(relatedProduct.image) ? relatedProduct.image[0] : relatedProduct.image}
                      alt={relatedProduct.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-1">
                      {relatedProduct.title}
                    </h3>

                    <p className="text-sm text-gray-600">
                      {relatedProduct.subtitle}
                    </p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <QuickInquiry
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
      />

      {/* Lab Report Dialog */}
      <Dialog open={labCertificateOpen} onOpenChange={setLabCertificateOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{product.title} Lab Report</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh]">
            <iframe
              src={labReportUrl}
              className="w-full h-full border-0"
              title={`${product.title} Lab Report`}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetail;
